import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createArtist, updateArtist } from '../redux/slices/artistsSlice';
import { fetchOrganizers } from '../redux/slices/organizersSlice';
import { toast } from 'react-toastify';
import LanguageSelector from './LanguageSelector';
import validator from 'validator';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import FileDropzone from './FileDropzone';
import { MAX_IMAGE_SIZE_1MB } from './../constants';
import { ACCEPTED_FORMATS } from './../constants';

const empty = (value) => !validator.isEmpty(value, { ignore_whitespace: true });

function ArtistsDrawer({ open, onClose, artist }) {
  const { t } = useTranslation();

  const [selectedLanguage, setSelectedLanguage] = useState('ro');
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.artists.isLoading);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedProfileUrl, setSelectedProfileUrl] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [description, setDescription] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionDe, setDescriptionDe] = useState('');
  const [descriptionHu, setDescriptionHu] = useState('');
  const [selectedOrganizerId, setSelectedOrganizerId] = useState('');
  const [index, setIndex] = useState(true);
  const organizers = useSelector((state) => state.organizers.data);

  const descriptionMap = {
    ro: [description, setDescription],
    en: [descriptionEn, setDescriptionEn],
    de: [descriptionDe, setDescriptionDe],
    hu: [descriptionHu, setDescriptionHu],
  };

  const [descriptionValue, setDescriptionValue] = descriptionMap[selectedLanguage] ?? ['', () => {}];

  const createDisabled = useMemo(
    () => isLoading || !empty(firstName) || description.length > 2048 || !empty(lastName) || !selectedProfile,
    [isLoading, firstName, description, lastName, selectedProfile]
  );
  const updateDisabled = useMemo(
    () => isLoading || !empty(firstName) || !empty(lastName) || description.length > 2048,
    [isLoading, firstName, lastName, description]
  );

  const onProfile = useCallback(
    (file) => {
      setSelectedProfile(file);
    },
    [setSelectedProfile]
  );

  const onDrawerClose = () => {
    onClose();
    setSelectedLanguage('ro');
    setFirstName('');
    setLastName('');
    setDescription('');
    setDescriptionEn('');
    setDescriptionDe('');
    setDescriptionHu('');
    setSelectedProfileUrl('');
    setSelectedProfile(null);
    setSelectedOrganizerId('');
    setIndex(true);
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchOrganizers());
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (artist) {
      const {
        first_name,
        last_name,
        description,
        description_en,
        description_de,
        description_hu,
        organizers_id,
        profile_image_url,
        index_for_search,
      } = artist;
      setFirstName(first_name);
      setLastName(last_name);
      setDescription(description || '');
      setDescriptionEn(description_en || '');
      setDescriptionDe(description_de || '');
      setDescriptionHu(description_hu || '');
      setSelectedOrganizerId(organizers_id || '');
      setSelectedProfileUrl(profile_image_url);
      setIndex(Boolean(index_for_search));
    } else {
      setSelectedOrganizerId('');
    }
  }, [artist]);

  const onCreateArtist = () => {
    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      description: description.trim(),
      description_en: descriptionEn.trim(),
      description_de: descriptionDe.trim(),
      description_hu: descriptionHu.trim(),
      organizers_id: selectedOrganizerId,
      profile: selectedProfile,
      index_for_search: index,
    };
    dispatch(createArtist(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('Artist created successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  const onUpdateArtist = () => {
    const payload = {
      artistId: artist.id,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      description: description.trim(),
      description_en: descriptionEn.trim(),
      description_de: descriptionDe.trim(),
      description_hu: descriptionHu.trim(),
      organizers_id: selectedOrganizerId,
      profile: selectedProfile,
      index_for_search: index,
    };
    dispatch(updateArtist(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('Artist updated successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  return (
    <Drawer anchor="right" open={open}>
      <Toolbar />
      <Toolbar>
        <Typography variant="button">{!artist ? t('Create Artist') : t('Edit Artist')}</Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ width: 400, pl: 2, pr: 2 }}>
        <Box component="form">
          <TextField
            required
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            fullWidth
            variant="standard"
            margin="normal"
            size="small"
            name="first_name"
            label={t('First Name')}
          />
          <TextField
            required
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            fullWidth
            variant="standard"
            margin="normal"
            size="small"
            name="last_name"
            label={t('Last Name')}
          />
          <FormControl fullWidth variant="standard" margin="normal" size="small">
            <InputLabel id="artist-organizer-label">{t('Organizer')}</InputLabel>
            <Select
              value={selectedOrganizerId}
              onChange={(event) => setSelectedOrganizerId(event.target.value)}
              labelId="artist-organizer-label"
              id="artist-organizer"
              label={t('Organizer')}
            >
              <MenuItem value="">
                <em>-</em>
              </MenuItem>
              {organizers.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LanguageSelector language={selectedLanguage} setLanguage={setSelectedLanguage} isDrawer={true} />
          <TextField
            multiline
            minRows={5}
            maxRows={10}
            value={descriptionValue}
            onChange={(event) => setDescriptionValue(event.target.value.replace('\n', '\r\n'))}
            fullWidth
            variant="standard"
            margin="normal"
            size="small"
            name="description"
            label={t('Description')}
            helperText={t('Max length is 2048 characters')}
          />
          <Box mt={2} mb={2}>
            <Typography gutterBottom variant="subtitle1">
              {t('Profile')}
            </Typography>
            <FileDropzone
              sx={{ height: 300 }}
              previewUrl={selectedProfileUrl}
              preview
              fileTypes={ACCEPTED_FORMATS}
              maxSize={MAX_IMAGE_SIZE_1MB}
              onDrop={onProfile}
            />
          </Box>
          <Typography variant="body2">{t('Max size: 10MB')}</Typography>
          <Typography variant="body2">{t('File format: JPEG,JPG,PNG')}</Typography>
          <FormGroup sx={{ display: 'block', mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={index} onChange={(event) => setIndex(event.target.checked)} />}
              label={t('Index in main site')}
            />
          </FormGroup>
        </Box>
      </Box>
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Grid columnSpacing={2} container>
          <Grid xs={6} item>
            <Button fullWidth variant="outlined" onClick={onDrawerClose}>
              {t('Cancel')}
            </Button>
          </Grid>
          <Grid xs={6} item>
            <Button
              disabled={!artist ? createDisabled : updateDisabled}
              fullWidth
              variant="contained"
              onClick={!artist ? onCreateArtist : onUpdateArtist}
            >
              {!artist ? t('Create') : t('Update')}
              {isLoading && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}

export default ArtistsDrawer;
